import type { RootState } from '../store'
import { isProductEnabled } from '../environment/featureFlags'
import {
  bboxProductDetailsInterface,
  getRegionBasedServices,
} from './sideNavRegionsBoundingBox'

/**
 * IS THIS PRODUCT AVAILABLE TO THIS USER?
 *
 * The one place that answers that question, so no call site has to re-derive the
 * user's region and thread it into isProductEnabled() by hand.
 *
 * Two INDEPENDENT axes, deliberately kept separate:
 *
 *   isEnabled(id)    build-time feature flag - is it switched on in this build?
 *   isInRegion(id)   region coverage - is it offered where the user is?
 *   isAvailable(id)  both
 *
 * Callers opt into the region axis knowingly. That is not just for readability:
 * the region product sets in sideNavRegionsBoundingBox.ts are keyed by SIDE-NAV
 * ids only, so a product key that has no nav item is in no region set at all and
 * isInRegion() would be false everywhere for it. Defaulting the region axis on
 * would hard-block every such product.
 *
 * Layering, deliberately: src/environment/featureFlags.ts stays React-free and
 * redux-free (its test re-requires it under jest.isolateModules with a synthetic
 * matrix, and MenuSelection imports it inside a useEffect). This module adds
 * region derivation; src/hooks/useProductEnabled.ts adds the React binding.
 *
 * `import type { RootState }` must keep the `type` keyword. Babel compiles
 * file-by-file and cannot prove a plain named import is type-only, so `import
 * type` is what guarantees this module has no runtime edge into store.ts.
 */
export interface ProductGate {
  /** Region from getRegionBasedServices(). Never '' - always at least 'Default'. */
  readonly region: string
  /** The region's product id set, for the rare caller that needs it raw. */
  readonly products: ReadonlySet<string>
  /** Build-time feature flag only. DEFAULT-OPEN for ids absent from the matrix. */
  isEnabled(menuId?: string | null): boolean
  /** Region coverage only. Nav-keyed ids only - see the note above. */
  isInRegion(menuId?: string | null): boolean
  /** isEnabled && isInRegion. */
  isAvailable(menuId?: string | null): boolean
}

let cachedKey: string | null = null
let cachedGate: ProductGate | null = null

/**
 * Build (or return a cached) gate for a location.
 *
 * MEMOISED, size 1, keyed on the stringified coordinates. This is what makes the
 * gate safe to call from a render body: getRegionBasedServices() runs
 * point-in-polygon over every bounding box, and SideNavigation calls it from
 * inside a RECURSIVE component. Without the cache, every consumer would repeat
 * that work on every render.
 *
 * Size 1 is sufficient and correct - there is one default location at a time,
 * and every caller in a single commit asks the same question.
 *
 * Keyed on the primitives, not the object, because:
 *   - setDefaultLocation spreads a new object on every dispatch, so identity
 *     changes even when the coordinates do not;
 *   - defaultLocation is typed `any`, is initialised to {}, and App.tsx's
 *     defaultLocationHelper can dispatch `undefined` for a user with zero
 *     locations - hence the `?? {}` guard, since getRegionBasedServices reads
 *     .longitude unguarded and would throw;
 *   - Location.latitude/longitude are STRINGS in src/interfaces/Location.ts but
 *     declared `number` by the local interface in sideNavRegionsBoundingBox.ts.
 *     String() on both sidesteps that contradiction for the cache key, while the
 *     raw object is still passed through unchanged so the existing coercion
 *     behaviour inside getRegionBasedServices is preserved exactly.
 *
 * Freezing the result also gives referential stability, which
 * getRegionBasedServices does not: it returns the same boundingBoxes[i] on a hit
 * but a freshly built object on the 'Default' fallback path. A stable gate is
 * safe to put in a dependency array.
 */
export function productGateFor(location: unknown): ProductGate {
  const loc = (location ?? {}) as { latitude?: unknown; longitude?: unknown }
  const key = `${String(loc.latitude)},${String(loc.longitude)}`
  if (key === cachedKey && cachedGate) return cachedGate

  const { region, products } = getRegionBasedServices(
    loc as never
  ) as bboxProductDetailsInterface

  cachedGate = Object.freeze({
    region,
    products: products as ReadonlySet<string>,
    isEnabled: (menuId?: string | null) => isProductEnabled(menuId, region),
    isInRegion: (menuId?: string | null) => !!menuId && products.has(menuId),
    isAvailable: (menuId?: string | null) =>
      !!menuId && products.has(menuId) && isProductEnabled(menuId, region),
  })
  cachedKey = key
  return cachedGate
}

/**
 * Snapshot gate from a redux state object, for callers that cannot use a hook.
 *
 * Several already reach for store.getState() directly (savedViews, AlertingSlice,
 * getActiveAlerts, CustomProfilesAPIService, SlideOverDialogEvents), and
 * src/hooks/useConfirm.tsx renders into a DETACHED createRoot with no redux
 * Provider, where any hook would throw. Use `productGate(store.getState())`
 * there.
 *
 * Note this is a snapshot: it does not re-run when the location changes. Prefer
 * useProductEnabled() inside components.
 */
export function productGate(state: RootState): ProductGate {
  return productGateFor(state?.user?.defaultLocation)
}

/** The user's current region string. Never '' - at least 'Default'. */
export function getCurrentRegion(state: RootState): string {
  return productGate(state).region
}

/** Flag-only check from a redux state snapshot. */
export function isProductEnabledFor(
  state: RootState,
  menuId?: string | null
): boolean {
  return productGate(state).isEnabled(menuId)
}

/** Flag AND region check from a redux state snapshot. */
export function isProductAvailableFor(
  state: RootState,
  menuId?: string | null
): boolean {
  return productGate(state).isAvailable(menuId)
}

/** Test seam only - drops the memo so a test can change locations freely. */
export function __resetProductGateCache(): void {
  cachedKey = null
  cachedGate = null
}
